import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {OFFER_STATUSES, type Offer} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {isFirstLoad} from 'utils/query';
import {usePath, usePaymentSettings, useFetchOffers, useFetchGuruWalkTours} from 'hooks';
import {useFetchEsimFeature} from 'hooks/esim/useFetchEsimFeature';
import {POSTHOG_EVENTS, useAnalytics} from 'analytics';
import {formatPrice} from 'components/upselling/price';
import {usePathToOfferView} from 'components/upselling/usePathToOfferView';
import {
  MOZIO_ORIGIN,
  buildGuruWalkOfferUrl,
  createMozioHref,
  getGuruWalkLanguage,
} from 'components/upselling/partnerLinks';
import {requestPartnerHandoff} from 'components/upselling/PartnerHandoff';
import {
  getLuggageOfferImage,
  isGuruWalkAvailable,
  isLuggageServiceOffer,
  isTimeBasedOffer,
} from 'utils/upselling';
import {useBookExperienceStore} from 'store/bookExperience';
import mozioImage from 'components/upselling/Mozio/mozio.webp';
import airaloBanner from 'components/upselling/assets/airalo-banner.webp?no-inline';

export type UpsellSource = 'offer' | 'guruwalk' | 'mozio' | 'esim';

export interface UpsellItem {
  id: string;
  source: UpsellSource;
  title: string;
  ctaLabel: string;
  onSelect: () => void;
  onAdd?: () => void;
  description?: string;
  imageUrl?: string;
  price?: string;
  showFromPrefix?: boolean;
}

export function useUpsellItems(): {items: UpsellItem[]; isLoading: boolean} {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {data: reservation} = useReservation();
  const {data: summary} = useSummary();
  const {currencySymbol} = usePaymentSettings();
  const {track} = useAnalytics();
  const {getFullPathToOfferView} = usePathToOfferView();

  const housingId = reservation?.housing_id;

  const offersQuery = useFetchOffers({housingId});
  const paginatedOfferList = offersQuery.data;
  const offersLoading = isFirstLoad(offersQuery);

  const guruWalkLocality = isGuruWalkAvailable(summary)
    ? summary?.location?.city || summary?.location?.country?.name
    : undefined;
  const guruWalkQuery = useFetchGuruWalkTours({
    locality: guruWalkLocality ?? '',
    country: summary?.location?.country?.name ?? '',
    checkIn: summary?.check_in_date ?? '',
    checkOut: summary?.check_out_date ?? '',
    language: guruWalkLocality ? getGuruWalkLanguage(i18n.language) : '',
    limit: 10,
  });
  const guruWalkTours = guruWalkQuery.data ?? [];

  const isMozioActive = summary?.mozio_status === OFFER_STATUSES.active;
  const {isEsimEnabled: isEsimActive, isLoading: esimLoading} = useFetchEsimFeature();
  const isLoading = offersLoading || isFirstLoad(guruWalkQuery) || esimLoading;

  const setItemQuantity = useBookExperienceStore(state => state.setItemQuantity);

  const seedFirstItemQuantity = useCallback(
    (offer: Offer) => {
      if (isTimeBasedOffer(offer) || isLuggageServiceOffer(offer.category)) return;

      const firstItem = offer.items?.[0];
      if (!firstItem) return;

      const chosen = useBookExperienceStore.getState().itemQuantities[offer.id];
      if (Object.values(chosen ?? {}).some(quantity => quantity > 0)) return;

      setItemQuantity({offerId: offer.id, itemId: firstItem.id, quantity: 1});
    },
    [setItemQuantity],
  );

  return useMemo(() => {
    const items: UpsellItem[] = [];

    (paginatedOfferList?.results ?? []).forEach(offer => {
      const openOffer = () => {
        track(POSTHOG_EVENTS.RECOMMENDATIONS_CLICKED);
        navigate(getFullPathToOfferView(offer.id));
      };
      items.push({
        id: `offer-${offer.id}`,
        source: 'offer',
        title: offer.title,
        description: offer.highlight || offer.description,
        imageUrl: offer.image_url || getLuggageOfferImage(offer.category),
        price: isLuggageServiceOffer(offer.category)
          ? t('luggage_price_varies')
          : formatPrice(currencySymbol)({
              priceType: offer.price_type,
              items: offer.items,
            }),
        showFromPrefix:
          !isLuggageServiceOffer(offer.category) && (offer.items?.length ?? 0) > 1,
        ctaLabel: t('add'),
        onSelect: openOffer,
        onAdd: () => {
          seedFirstItemQuantity(offer);
          openOffer();
        },
      });
    });

    guruWalkTours.forEach(tour => {
      items.push({
        id: `guruwalk-${tour.id}`,
        source: 'guruwalk',
        title: tour.title,
        description: tour.description,
        imageUrl: tour.imageUrl ?? undefined,
        ctaLabel: t('book'),
        onSelect: () => {
          track(POSTHOG_EVENTS.PARTNER_CLICKED, {
            partner: 'guruwalk',
            partner_category: 'tours',
            housing_id: housingId,
            tour_id: tour.id,
            tour_title: tour.title,
          });
          requestPartnerHandoff(
            buildGuruWalkOfferUrl({tourPath: tour.url, reservationId: reservation?.id}),
          );
        },
      });
    });

    if (isMozioActive) {
      items.push({
        id: 'mozio',
        source: 'mozio',
        title: t('airport_city_transfers'),
        description: t('pre_book_your_ride_with_trusted_local_partners'),
        imageUrl: mozioImage,
        ctaLabel: t('book'),
        onSelect: () => {
          track(POSTHOG_EVENTS.PARTNER_CLICKED, {
            partner: 'mozio',
            partner_category: 'transportation',
            housing_id: housingId,
            destination: MOZIO_ORIGIN,
          });
          requestPartnerHandoff(createMozioHref());
        },
      });
    }

    if (isEsimActive) {
      items.push({
        id: 'esim',
        source: 'esim',
        title: t('esim_card_title'),
        description: t('esim_card_subtitle'),
        imageUrl: airaloBanner,
        price: t('airalo_banner.starting_price', {currencySymbol}),
        ctaLabel: t('view_plans'),
        onSelect: () => {
          track(POSTHOG_EVENTS.PARTNER_CLICKED, {
            partner: 'airalo',
            partner_category: 'esim',
            housing_id: housingId,
          });
          navigate(getFullPath(PATHS.esim.manage));
        },
      });
    }

    const order: Record<UpsellSource, number> = {
      offer: 0,
      mozio: 1,
      esim: 2,
      guruwalk: 3,
    };
    items.sort((a, b) => order[a.source] - order[b.source]);

    return {items, isLoading};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginatedOfferList,
    guruWalkQuery.data,
    isMozioActive,
    isEsimActive,
    currencySymbol,
    housingId,
    reservation?.id,
    isLoading,
    getFullPathToOfferView,
    seedFirstItemQuantity,
    t,
  ]);
}
